import { priceLine, type Catalog, type Product, type Selections, type Channel } from './domain';
export interface PriceImpact { productId: string; name: string; choices: string[]; before: (number | null)[]; after: (number | null)[]; defaultCup: boolean }
/** Compare one option at a time, plus bean/extra pairs (extra shots use the selected bean).
 * This is a review of representative cups, not every possible option combination. */
export function priceImpact(before?: Catalog, after?: Catalog): PriceImpact[] {
  const ids = new Set([...(before?.products ?? []), ...(after?.products ?? [])].map(p => p.id));
  const result: PriceImpact[] = [];
  for (const id of ids) {
    const old = before?.products.find(p => p.id === id), next = after?.products.find(p => p.id === id);
    const cases = new Map<string, { choices: string[]; patch?: Partial<Selections> }>();
    cases.set('default', { choices: [] });
    for (const [catalog, product] of [[before, old], [after, next]] as const) {
      if (!catalog || !product || product.archived) continue;
      const add = (patch: Partial<Selections>, choices: string[]) => cases.set(JSON.stringify(patch), { patch, choices });
      for (const beanId of product.beanIds) add({ beanId }, [catalog.ingredients.find(i => i.id === beanId)?.name ?? beanId]);
      for (const ice of product.iceLevels) add({ ice }, [`ice:${ice}`]);
      for (const sweetness of product.sweetnessLevels) add({ sweetness }, [`${sweetness}%`]);
      for (const packaging of ['takeaway', 'own_cup'] as const) add({ packaging }, [packaging]);
      for (const extra of product.extras) {
        add({ extras: [...new Set([...product.defaults.extras, extra.id])] }, [extra.name]);
        for (const beanId of product.beanIds) add({ beanId, extras: [...new Set([...product.defaults.extras, extra.id])] }, [catalog.ingredients.find(i => i.id === beanId)?.name ?? beanId, extra.name]);
      }
    }
    const quote = (catalog: Catalog | undefined, product: Product | undefined, channel: Channel, patch?: Partial<Selections>) => {
      if (!catalog || !product || product.archived) return null;
      const selections = { ...product.defaults, ...patch };
      try {
        const simulation = { ...catalog, products: catalog.products.map(p => ({ ...p, available: true })), ingredients: catalog.ingredients.map(i => ({ ...i, available: true })), packaging: catalog.packaging.map(p => ({ ...p, available: true })) };
        return priceLine(simulation, channel, { productId: id, quantity: 1, selections }).unitPrice;
      } catch { return null; }
    };
    for (const [key, test] of cases) {
      // A default option adds no information beyond the default cup row.
      if (test.patch && [old, next].every(p => !p || Object.entries(test.patch!).every(([k, v]) => JSON.stringify(p.defaults[k as keyof Selections]) === JSON.stringify(v)))) continue;
      const from = (['employee', 'public'] as const).map(ch => quote(before, old, ch, test.patch));
      const to = (['employee', 'public'] as const).map(ch => quote(after, next, ch, test.patch));
      if (JSON.stringify(from) !== JSON.stringify(to)) result.push({ productId: id, name: (next ?? old)!.name, choices: test.choices, before: from, after: to, defaultCup: key === 'default' });
    }
  }
  return result;
}
